import { useCallback, useState } from "react";
import {
  Button,
  Card,
  Field,
  H4,
  LabelledCheckbox,
  Layout,
  Title,
  useActions,
  useToast
} from "@introist/react-foundation/v2";

import { api, RouterOutput } from "services/rpc/RpcProvider";
import { CopyButton, TextInput } from "../../../../components/atoms";
import { useWorkspace } from "IdentityProvider";

const deriveTenant = (workspaceName: string) =>
  workspaceName.toLowerCase().replace(/[^a-z0-9]/g, "");

type Provider = "entra" | "google" | "okta" | "generic";

interface ProviderInfo {
  label: string;
  description: string;
  discoveryUrlMode: "auto-entra" | "fixed-google" | "auto-okta" | "manual";
  clientIdLabel: string;
  clientIdHint: string;
  clientSecretHint: string;
  appCreationHint: string;
  redirectUriHint: string;
  permissionsHint: string;
}

const PROVIDERS: Record<Provider, ProviderInfo> = {
  entra: {
    label: "Microsoft Entra ID",
    description: "Azure Active Directory (Microsoft Entra ID)",
    discoveryUrlMode: "auto-entra",
    clientIdLabel: "Application (client) ID",
    clientIdHint: "Found in your app registration overview in the Azure portal.",
    clientSecretHint:
      'Go to "Certificates & secrets" → "New client secret" in your app registration.',
    appCreationHint:
      'Go to portal.azure.com → "Azure Active Directory" → "App registrations" → "New registration". Set the name and select "Accounts in this organizational directory only".',
    redirectUriHint: 'Under "Authentication" → "Add a platform" → "Web", paste the callback URL.',
    permissionsHint:
      'Under "API permissions", confirm "openid", "email", and "profile" are listed under Microsoft Graph.'
  },
  google: {
    label: "Google Workspace",
    description: "Google Workspace / Google Identity",
    discoveryUrlMode: "fixed-google",
    clientIdLabel: "Client ID",
    clientIdHint: "Found in the Google Cloud Console under APIs & Services → Credentials.",
    clientSecretHint: "Found alongside the Client ID in the Google Cloud Console credentials page.",
    appCreationHint:
      'Go to console.cloud.google.com → "APIs & Services" → "Credentials" → "Create credentials" → "OAuth client ID". Choose "Web application".',
    redirectUriHint: 'Under "Authorized redirect URIs", add the callback URL.',
    permissionsHint:
      'Under "OAuth consent screen", ensure the "openid", "email", and "profile" scopes are added.'
  },
  okta: {
    label: "Okta",
    description: "Okta Identity Provider",
    discoveryUrlMode: "auto-okta",
    clientIdLabel: "Client ID",
    clientIdHint: "Found in your Okta application settings under General.",
    clientSecretHint: 'Found in your Okta application settings under "Client Credentials".',
    appCreationHint:
      'In your Okta admin console, go to Applications → "Create App Integration" → select "OIDC – OpenID Connect" and "Web Application".',
    redirectUriHint: 'Under "Sign-in redirect URIs", add the callback URL.',
    permissionsHint:
      'Under "Okta API Scopes", ensure "openid", "email", and "profile" are granted.'
  },
  generic: {
    label: "Generic OIDC",
    description: "Any OpenID Connect-compliant provider",
    discoveryUrlMode: "manual",
    clientIdLabel: "Client ID",
    clientIdHint: "Provided by your identity provider.",
    clientSecretHint: "Provided by your identity provider.",
    appCreationHint:
      'Create an OAuth 2.0 / OpenID Connect application in your identity provider. Select "Web" as the application type.',
    redirectUriHint: "Add the callback URL as an allowed redirect URI in your provider.",
    permissionsHint:
      'Ensure the "openid", "email", and "profile" scopes are enabled for the application.'
  }
};

const GOOGLE_DISCOVERY_URL = "https://accounts.google.com/.well-known/openid-configuration";

const buildDiscoveryUrl = (provider: Provider, providerIdentifier: string): string => {
  if (provider === "entra") {
    return `https://login.microsoftonline.com/${providerIdentifier}/v2.0/.well-known/openid-configuration`;
  }
  if (provider === "google") {
    return GOOGLE_DISCOVERY_URL;
  }
  if (provider === "okta") {
    const domain = providerIdentifier.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `https://${domain}/.well-known/openid-configuration`;
  }
  return providerIdentifier;
};

type WizardState = {
  provider: Provider;
  providerIdentifier: string;
  clientId: string;
  clientSecret: string;
  forceTenant: boolean;
  autoCreateUsers: boolean;
};

export const SsoV2SettingsRoute = () => {
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const workspace = useWorkspace();

  const { data: ssoV2Config, refetch: reloadConfig } = api.workspaces.ssoV2.find.useQuery({});
  const createMutation = api.workspaces.ssoV2.create.useMutation();
  const deleteMutation = api.workspaces.ssoV2.delete.useMutation();

  const [step, setStep] = useState(0);
  const [wizard, setWizard] = useState<WizardState>({
    provider: "entra",
    providerIdentifier: "",
    clientId: "",
    clientSecret: "",
    forceTenant: false,
    autoCreateUsers: false
  });

  const providerInfo = PROVIDERS[wizard.provider];

  const tenant = ssoV2Config?.tenant ?? deriveTenant(workspace?.name ?? "");
  const callbackUrl = `https://auth.introist.com/v2/sso/${tenant}/callback`;

  const onEnable = useCallback(async () => {
    const discoveryUrl = buildDiscoveryUrl(wizard.provider, wizard.providerIdentifier);
    await createMutation
      .mutateAsync({
        provider: wizard.provider,
        clientId: wizard.clientId,
        clientSecret: wizard.clientSecret,
        discoveryUrl,
        forceTenant: wizard.forceTenant,
        autoCreateUsers: wizard.autoCreateUsers
      })
      .then(async () => {
        await reloadConfig();
        toast.success("SSO v2 enabled");
        setStep(0);
      })
      .catch(() => toast.error("Failed to enable SSO v2"));
  }, [wizard, createMutation, reloadConfig, toast]);

  const onDisable = onConfirmAction(
    async () => {
      await deleteMutation
        .mutateAsync({})
        .then(async () => {
          await reloadConfig();
          toast.success("SSO v2 disabled");
        })
        .catch(() => toast.error("Failed to disable SSO v2"));
    },
    {
      title: "Disable OIDC Single sign-on",
      description: "Are you sure you want to disable SSO v2? Configuration will be removed."
    }
  );

  if (ssoV2Config !== undefined && ssoV2Config !== null) {
    return <SsoV2Summary config={ssoV2Config} onDisable={onDisable} />;
  }

  return (
    <Card spacing="xLarge">
      <Layout.Group vertical gap="large">
        <Layout.Group vertical gap="small">
          <H4>OIDC Single Sign-On (v2)</H4>
          <Title>
            Connect Microsoft Entra ID, Google Workspace, Okta, or any OIDC-compliant provider.
          </Title>
        </Layout.Group>

        {/* Step 0 – Choose provider */}
        {step === 0 && (
          <Layout.Group vertical gap="large">
            <Title>Step 1 of 7 — Choose your identity provider</Title>
            <Layout.Group vertical gap="small">
              {(Object.keys(PROVIDERS) as Provider[]).map(p => (
                <Button
                  key={p}
                  variant={wizard.provider === p ? "filled" : "outlined"}
                  onClick={() => setWizard(w => ({ ...w, provider: p }))}
                >
                  {PROVIDERS[p].label}
                </Button>
              ))}
            </Layout.Group>
            <Button onClick={() => setStep(1)}>Continue</Button>
          </Layout.Group>
        )}

        {/* Step 1 – Create the OAuth app */}
        {step === 1 && (
          <Layout.Group vertical gap="large">
            <Title>Step 2 of 7 — Create an OAuth application in {providerInfo.label}</Title>
            <Title>{providerInfo.appCreationHint}</Title>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={() => setStep(2)}>I've created the app</Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 2 – Set redirect URI */}
        {step === 2 && (
          <Layout.Group vertical gap="large">
            <Title>Step 3 of 7 — Set the redirect URI</Title>
            <Title>{providerInfo.redirectUriHint}</Title>
            <Field title="Callback URL (paste this into your provider)">
              <TextInput
                size="small"
                readOnly
                value={callbackUrl}
                endAdornment={<CopyButton textToCopy={callbackUrl} />}
              />
            </Field>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)}>I've set the redirect URI</Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 3 – Provider identifier */}
        {step === 3 && providerInfo.discoveryUrlMode !== "fixed-google" && (
          <Layout.Group vertical gap="large">
            <Title>Step 4 of 7 — Provider identifier</Title>
            <Field
              title={
                providerInfo.discoveryUrlMode === "auto-entra"
                  ? "Directory (tenant) ID"
                  : providerInfo.discoveryUrlMode === "auto-okta"
                    ? "Your Okta domain (e.g. mycompany.okta.com)"
                    : "Discovery URL"
              }
            >
              <TextInput
                size="small"
                value={wizard.providerIdentifier}
                onChange={val => setWizard(w => ({ ...w, providerIdentifier: val }))}
                placeholder={
                  providerInfo.discoveryUrlMode === "auto-entra"
                    ? "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    : providerInfo.discoveryUrlMode === "auto-okta"
                      ? "mycompany.okta.com"
                      : "https://..."
                }
              />
            </Field>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                disabled={!wizard.providerIdentifier}
                onClick={() => setStep(4)}
              >
                Continue
              </Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Skip step 3 for Google (fixed discovery URL) */}
        {step === 3 && providerInfo.discoveryUrlMode === "fixed-google" && (
          <Layout.Group vertical gap="large">
            <Title>Step 4 of 7 — Discovery URL</Title>
            <Title>
              Google Workspace uses a fixed discovery URL. No input needed here.
            </Title>
            <Field title="Discovery URL">
              <TextInput size="small" readOnly value={GOOGLE_DISCOVERY_URL} endAdornment={<CopyButton textToCopy={GOOGLE_DISCOVERY_URL} />} />
            </Field>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={() => setStep(4)}>Continue</Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 4 – Client ID */}
        {step === 4 && (
          <Layout.Group vertical gap="large">
            <Title>Step 5 of 7 — {providerInfo.clientIdLabel}</Title>
            <Title>{providerInfo.clientIdHint}</Title>
            <Field title={providerInfo.clientIdLabel}>
              <TextInput
                size="small"
                value={wizard.clientId}
                onChange={val => setWizard(w => ({ ...w, clientId: val }))}
                placeholder="Paste your client ID here"
              />
            </Field>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button disabled={!wizard.clientId} onClick={() => setStep(5)}>
                Continue
              </Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 5 – Client Secret */}
        {step === 5 && (
          <Layout.Group vertical gap="large">
            <Title>Step 6 of 7 — Client secret</Title>
            <Title>{providerInfo.clientSecretHint}</Title>
            <Field title="Client secret">
              <TextInput
                size="small"
                type="password"
                value={wizard.clientSecret}
                onChange={val => setWizard(w => ({ ...w, clientSecret: val }))}
                placeholder="Paste your client secret here"
              />
            </Field>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(4)}>
                Back
              </Button>
              <Button disabled={!wizard.clientSecret} onClick={() => setStep(6)}>
                Continue
              </Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 6 – Permissions (informational) */}
        {step === 6 && (
          <Layout.Group vertical gap="large">
            <Title>Step 7 of 7 — Verify permissions</Title>
            <Title>{providerInfo.permissionsHint}</Title>
            <Title>
              Required scopes: <strong>openid</strong>, <strong>email</strong>,{" "}
              <strong>profile</strong>
            </Title>
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(5)}>
                Back
              </Button>
              <Button onClick={() => setStep(7)}>I've configured permissions</Button>
            </Layout.Group>
          </Layout.Group>
        )}

        {/* Step 7 – Options and activate */}
        {step === 7 && (
          <Layout.Group vertical gap="large">
            <Title>Final step — Options and activation</Title>
            <LabelledCheckbox
              checked={wizard.autoCreateUsers}
              label="Auto-create user accounts (Just-in-time provisioning)"
              onChange={val => setWizard(w => ({ ...w, autoCreateUsers: val }))}
            />
            <LabelledCheckbox
              checked={wizard.forceTenant}
              label="Force SSO — users must sign in via SSO only"
              onChange={val => setWizard(w => ({ ...w, forceTenant: val }))}
            />
            <Layout.Group>
              <Button variant="blended" onClick={() => setStep(6)}>
                Back
              </Button>
              <Button onClickWithLoading={onEnable}>Enable SSO v2</Button>
            </Layout.Group>
          </Layout.Group>
        )}
      </Layout.Group>
    </Card>
  );
};

type SsoV2SummaryProps = {
  config: NonNullable<RouterOutput["workspaces"]["ssoV2"]["find"]>;
  onDisable: () => void;
};

const SsoV2Summary = ({ config, onDisable }: SsoV2SummaryProps) => {
  const callbackUrl = `https://auth.introist.com/v2/sso/${config.tenant}/callback`;

  const providerLabel = PROVIDERS[config.provider as Provider]?.label ?? config.provider;

  return (
    <Card spacing="xLarge">
      <Layout.Group vertical gap="large">
        <Layout.Group gap="small" justifyContent="space-between">
          <H4>OIDC Single Sign-On (v2) — Active</H4>
          <Button variant="blended" onClick={onDisable}>
            Disable
          </Button>
        </Layout.Group>

        <Field title="Provider">
          <TextInput size="small" readOnly value={providerLabel} />
        </Field>

        <Field title="Client ID">
          <TextInput
            size="small"
            readOnly
            value={`${config.clientId.slice(0, 8)}${"•".repeat(Math.max(0, config.clientId.length - 8))}`}
          />
        </Field>

        <Field title="Callback URL">
          <TextInput
            size="small"
            readOnly
            value={callbackUrl}
            endAdornment={<CopyButton textToCopy={callbackUrl} />}
          />
        </Field>

      </Layout.Group>
    </Card>
  );
};
